/**
 * Fiscal Printer - ESC/POS receipt printing protocol (Novitus v1.13)
 */
import { CR } from './constants';
import { textToBytes } from './encoding';
import { sendCommand } from './transport';

/**
 * Cancel current transaction via ESC/POS (action=0).
 */
export async function cancelTransactionESCPOS(port) {
  try {
    await sendCommand(port, '0$e', true);
    await new Promise(r => setTimeout(r, 150));
  } catch (e) {
    // no transaction to cancel or cancel failed
  }
}

/**
 * USUNIETO (2026-08): printReceiptESCPOS - druk paragonu fiskalnego po ESC/POS.
 *
 * Paragony fiskalne drukuje WYLACZNIE protokol XML (Novitus Deon) - patrz
 * fiscalPrinterService.printReceipt, ktory na protokole innym niz XML odmawia
 * druku paragonu. Galaz ESC/POS drukowala paragon BEZ rabatow i BEZ kaucji,
 * a transakcja i tak konczyla jako niezafiskalizowana. Ten modul obsluguje
 * odtad wylacznie dokumenty NIEFISKALNE i raporty.
 */

/**
 * Print daily (Z) report via ESC/POS.
 */
export async function printDailyReportESCPOS(port) {
  await sendCommand(port, '2$r', true);
}

/**
 * Open cash drawer via ESC/POS.
 */
export async function openDrawerESCPOS(port) {
  await sendCommand(port, '1$d', false);
}

/**
 * Print periodic report via ESC/POS.
 */
export async function printPeriodicReportESCPOS(port, dateFrom, dateTo) {
  const from = dateFrom.replace(/-/g, '');
  const to   = dateTo.replace(/-/g, '');
  const cmdBytes = [
    ...textToBytes('0$R'),
    ...textToBytes(from),
    CR,
    ...textToBytes(to)
  ];
  await sendCommand(port, cmdBytes, true);
}

/**
 * Print a non-fiscal document via ESC/POS (Novitus v1.13, section 3.9).
 */
export async function printNonFiscalDocumentESCPOS(port, decoder, status, documentData) {
  const { title = 'DOKUMENT', lines = [] } = documentData;

  if (!status.connected) {
    return { success: false, error: 'Drukarka nie odpowiada na zapytanie o status' };
  }

  if (status.inTransaction) {
    await cancelTransactionESCPOS(port);
    await new Promise(r => setTimeout(r, 150));
  }

  const PRINTOUT_NUM = 200;

  // 1. Open non-fiscal document
  await sendCommand(port, `0;${PRINTOUT_NUM};0;00$w`, true);
  await new Promise(r => setTimeout(r, 150));

  const allLines = [{ text: title, line: 0 }];
  let lineIdx = 1;
  for (const line of lines) {
    if (lineIdx > 3) break;
    const lineText = String(line).substring(0, 42);
    if (lineText.trim() !== '') {
      allLines.push({ text: lineText, line: lineIdx++ });
    }
  }

  // 2. Print lines
  for (const item of allLines) {
    const lineBytes = [
      ...textToBytes(`${PRINTOUT_NUM};${item.line};0;0;0;1;0$w`),
      ...textToBytes(item.text),
      CR
    ];
    await sendCommand(port, lineBytes, true);
    await new Promise(r => setTimeout(r, 150));
  }

  // 3. Close document
  await sendCommand(port, `1;${PRINTOUT_NUM};0;00$w`, true);
  await new Promise(r => setTimeout(r, 150));

  return { success: true, message: 'Dokument niefiskalny wydrukowany' };
}

/**
 * Get printer status via ESC/POS ENQ byte.
 */
export async function getStatusESCPOS(port, sendENQFn) {
  const response = await sendENQFn(port);
  if (response && response.length > 0) {
    const statusByte = response[0];
    return {
      connected: true,
      responding: true,
      statusByte,
      fiscal: (statusByte & 0x08) !== 0,
      commandOK: (statusByte & 0x04) !== 0,
      inTransaction: (statusByte & 0x02) !== 0,
      lastTransOK: (statusByte & 0x01) !== 0,
      raw: Array.from(response).map(b => b.toString(16)).join(' ')
    };
  }
  return { connected: false, responding: false, error: 'Drukarka nie odpowiada na zapytanie statusu' };
}
